import { FieldMetadata } from '../types/extractedTerms';

/**
 * Format a field value for display based on its metadata
 */
export function formatFieldValue(value: any, metadata: FieldMetadata): string {
  if (value === null || value === undefined) {
    return '—';
  }

  switch (metadata.type) {
    case 'number':
      return formatNumber(value, metadata.format);

    case 'boolean':
      return value ? 'Yes' : 'No';

    case 'enum':
    case 'string':
      return String(value);

    default:
      return String(value);
  }
}

/**
 * Format a number value based on its format type
 */
function formatNumber(value: number, format?: string): string {
  if (typeof value !== 'number' || isNaN(value)) {
    return String(value);
  }

  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);

    case 'percentage':
      return `${value.toFixed(2)}%`;

    case 'number':
    default:
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
  }
}

/**
 * Get filter operator display name
 * For date fields, use "Before"/"After" instead of "Less Than"/"Greater Than"
 */
export function getOperatorDisplayName(operator: string, fieldType?: string): string {
  const operatorMap: Record<string, string> = {
    equals: 'Equals',
    not_equals: 'Not Equals',
    greater_than: fieldType === 'date' ? 'After' : 'Greater Than',
    less_than: fieldType === 'date' ? 'Before' : 'Less Than',
    between: 'Between',
    contains: 'Contains',
    in: 'In',
  };

  return operatorMap[operator] || operator;
}

/**
 * Get appropriate input component type for a field
 */
export function getInputComponentType(metadata: FieldMetadata): 'text' | 'number' | 'select' | 'checkbox' {
  switch (metadata.type) {
    case 'number':
      return 'number';
    case 'boolean':
      return 'checkbox';
    case 'enum':
      return 'select';
    case 'string':
    default:
      return 'text';
  }
}
